import React, { useEffect } from "react";
import { Container } from "react-bootstrap";

import "./Experience.css";

function Experience() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px 60px 0px"
      }
    );

    const elements = document.querySelectorAll(
      ".experience-section .scroll-reveal"
    );

    elements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      elements.forEach((element) => {
        observer.unobserve(element);
      });

      observer.disconnect();
    };
  }, []);

  const responsibilities = [
    "Provide day-to-day IT support across a multi-company environment, assisting users with hardware, software, connectivity, accounts, printers, and business applications.",
    "Install, configure, maintain, and troubleshoot desktop computers, laptops, printers, network equipment, CCTV systems, and other workplace technology.",
    "Support routers, switches, wireless networks, internet connectivity, and communication links across multiple company locations.",
    "Install, configure, and maintain CCTV cameras, recording systems, network connections, and monitoring access across the supported companies.",
    "Manage and support VoIP services and IP phones, including device setup, user extensions, connectivity, troubleshooting, and ongoing maintenance.",
    "Administer and support business platforms such as Lark and Odoo, including user access, configuration, troubleshooting, and operational assistance.",
    "Install operating systems, business applications, drivers, updates, and required utilities on user devices.",
    "Investigate account-access problems, failed login attempts, suspicious emails, spam activity, and other user-reported security concerns.",
    "Handle user onboarding and offboarding, including account creation, device preparation, application access, extension assignment, and equipment handover.",
    "Support data backup, software updates, device maintenance, and general IT continuity across the multi-company environment.",
    "Coordinate hardware repairs, replacements, installations, and procurement requirements while maintaining IT asset and equipment records.",
    "Work with users, departments, vendors, and service providers to resolve technical issues, restore services, and improve recurring IT processes."
  ];

  const operationalAreas = [
    {
      title: "Multi-Company Support",
      description:
        "Support users, systems, devices, and business applications across multiple companies and operating environments."
    },
    {
      title: "Networking",
      description:
        "Maintain routers, switches, wireless connectivity, internet access, and communication links across company locations."
    },
    {
      title: "CCTV Infrastructure",
      description:
        "Install and maintain CCTV cameras, recording systems, network connectivity, and monitoring access."
    },
    {
      title: "VoIP & Telephony",
      description:
        "Support VoIP services and IP phones, including extension setup, device configuration, troubleshooting, and maintenance."
    },
    {
      title: "Business Systems",
      description:
        "Administer and support platforms such as Lark and Odoo while assisting users with access and daily operations."
    },
    {
      title: "Technical Operations",
      description:
        "Handle hardware installations, software deployment, repairs, upgrades, asset records, and ongoing IT maintenance."
    }
  ];

  const coursework = [
    "Computer Networks",
    "Operating Systems",
    "Cybersecurity Fundamentals",
    "Software Engineering",
    "Cryptography",
    "Database Management"
  ];

  return (
    <section
      className="experience-section"
      id="experience"
    >
      <Container>
        <header className="experience-header scroll-reveal">
          <span className="experience-eyebrow">
            Experience
          </span>

          <h2 className="experience-title">
            Professional <span>Experience</span>
          </h2>

          <p className="experience-subtitle">
            Practical experience supporting users, business systems,
            networking, hardware, CCTV, VoIP, software installations,
            and day-to-day IT operations across a multi-company environment.
          </p>
        </header>

        <div className="experience-content">
          <article className="experience-entry scroll-reveal">
            <div className="experience-entry-side">
              <span className="experience-status">
                <span aria-hidden="true" />
                Current role
              </span>

              <p className="experience-date">
                June 2025 – Present
              </p>

              <p className="experience-location">
                Nairobi, Kenya
              </p>
            </div>

            <div className="experience-entry-main">
              <header className="experience-entry-header">
                <span className="experience-entry-type">
                  Multi-Company IT Support &amp; Administration
                </span>

                <h3>IT Officer</h3>

                <p className="experience-company">
                  Runda Heavy Industries Limited
                </p>

                <p className="experience-summary">
                  Supporting daily IT operations across a multi-company
                  environment, including user support, networking, hardware,
                  CCTV, VoIP and IP telephony, software installations,
                  business applications, account access, and general IT
                  administration.
                </p>
              </header>

              <section className="experience-responsibilities">
                <h4>Key responsibilities</h4>

                <ul>
                  {responsibilities.map((responsibility) => (
                    <li key={responsibility}>
                      {responsibility}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="experience-impact">
                <h4>Operational areas</h4>

                <div className="experience-impact-grid">
                  {operationalAreas.map((area) => (
                    <div
                      key={area.title}
                      className="experience-impact-item"
                    >
                      <strong>{area.title}</strong>
                      <p>{area.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </article>

          <section className="education-section scroll-reveal">
            <div className="education-side">
              <span className="education-label">
                Education
              </span>

              <p>2020 – 2024</p>
            </div>

            <div className="education-main">
              <span className="education-type">
                Bachelor&apos;s Degree
              </span>

              <h3>
                Bachelor of Science in Information Technology
              </h3>

              <p className="education-institution">
                Maseno University
              </p>

              <p className="education-description">
                Built a broad foundation in networking, operating systems,
                cybersecurity, software development, databases, and
                information systems.
              </p>

              <div className="education-coursework">
                <h4>Relevant coursework</h4>

                <div className="education-course-list">
                  {coursework.map((course) => (
                    <span key={course}>
                      {course}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </Container>
    </section>
  );
}

export default Experience;